import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '@/components/dashboard/PageHeader';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, RefreshCw, Download } from 'lucide-react';
import PaymentsStatsBar from '@/components/payments/PaymentsStatsBar';
import PaymentsByJobCard from '@/components/payments/PaymentsByJobCard';
import PaymentsHistoryList from '@/components/payments/PaymentsHistoryList';
import {
  fetchPaymentsData,
  flattenPayments,
  buildPaymentsByJob,
  computePaymentStats,
  applyPaymentFilter,
  paymentsToCsv,
  downloadCsv,
} from '@/lib/paymentsData';
import { toast } from 'sonner';

export default function PaymentsPage() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [view, setView] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const data = await fetchPaymentsData();
      setJobs(data);
    } catch (e) {
      setError(e?.message || t('payments.loadError'));
    } finally {
      if (silent) setRefreshing(false);
      else setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const allInvoiceRows = useMemo(() => flattenPayments(jobs), [jobs]);
  const byJob = useMemo(() => buildPaymentsByJob(jobs), [jobs]);
  const stats = useMemo(
    () => computePaymentStats(allInvoiceRows, byJob),
    [allInvoiceRows, byJob]
  );
  const filteredRows = useMemo(
    () => applyPaymentFilter(allInvoiceRows, { search, view }),
    [allInvoiceRows, search, view]
  );

  const handleJobUpdated = (updatedJob) => {
    if (!updatedJob?.id) return;
    setJobs((cur) =>
      cur.map((j) => (j.id === updatedJob.id ? { ...j, ...updatedJob } : j))
    );
  };

  const handleExport = () => {
    const csv = paymentsToCsv(filteredRows);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`kocore-payments-${view}-${stamp}.csv`, csv);
    toast.success(t('payments.exportedToast', { count: filteredRows.length }));
  };

  const handlePickJob = (jobRow) => {
    setView('all');
    setSearch(jobRow.job_name);
  };

  return (
    <div data-testid="payments-page">
      <PageHeader
        eyebrow={t('payments.eyebrow')}
        title={t('payments.title')}
        subtitle={t('payments.subtitle')}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => load({ silent: true })}
              disabled={loading || refreshing}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="payments-refresh"
            >
              <RefreshCw size={14} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              {t('payments.refresh')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={filteredRows.length === 0}
              className="border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="payments-export"
            >
              <Download size={14} className="mr-2" />
              {t('payments.exportCsv')}
            </Button>
          </div>
        }
      />

      {error ? (
        <Alert
          className="mb-5 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="payments-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      <PaymentsStatsBar stats={stats} view={view} onView={setView} />

      {/* The By Job panel sits beside the list only from 2xl: beside a
          ~550px list at xl it squeezed the job-name column to "Post-F…". */}
      <div className="grid grid-cols-1 2xl:grid-cols-3 gap-4">
        <div className="2xl:col-span-2">
          <PaymentsHistoryList
            rows={filteredRows}
            loading={loading}
            jobs={jobs}
            view={view}
            search={search}
            onSearch={setSearch}
            onChanged={handleJobUpdated}
          />
        </div>
        <div>
          <PaymentsByJobCard rows={byJob} onPick={handlePickJob} />
        </div>
      </div>
    </div>
  );
}
